import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useWallet } from '@solana/wallet-adapter-react';
import { CheckIcon, ChevronDownIcon } from '@heroicons/react/24/solid';
import { http } from '../../api/http';
import { usePracticeMatchConnection, PracticeDifficulty } from '../../state/practiceMatchStore';

interface BaseJoker {
  id: string;
  name: string;
  description: string;
  rarity: string;
  effects: Array<{ kind: string; [key: string]: unknown }>;
}

const DIFFICULTY_CONFIG: Record<
  PracticeDifficulty,
  {
    label: string;
    description: string;
    color: string;
    bgColor: string;
    borderColor: string;
  }
> = {
  easy: {
    label: 'Easy',
    description: 'Random AI decisions, no starting joker',
    color: 'text-green-400',
    bgColor: 'bg-green-500/20',
    borderColor: 'border-green-500/50',
  },
  medium: {
    label: 'Medium',
    description: 'Smart AI with basic strategy, random starting joker',
    color: 'text-yellow-400',
    bgColor: 'bg-yellow-500/20',
    borderColor: 'border-yellow-500/50',
  },
  hard: {
    label: 'Hard',
    description: 'Optimal AI play with strategic joker selection',
    color: 'text-red-400',
    bgColor: 'bg-red-500/20',
    borderColor: 'border-red-500/50',
  },
};

export const PracticeModePage: React.FC = () => {
  const navigate = useNavigate();
  const { publicKey } = useWallet();
  const wallet = publicKey?.toBase58();
  const { connect, connecting, error: connectionError } = usePracticeMatchConnection();

  const [difficulty, setDifficulty] = useState<PracticeDifficulty>('medium');
  const [availableJokers, setAvailableJokers] = useState<BaseJoker[]>([]);
  const [selectedJoker, setSelectedJoker] = useState<string | null>(null);
  const [loadingJokers, setLoadingJokers] = useState(false);
  const [jokerSectionExpanded, setJokerSectionExpanded] = useState(false);
  const [disableTimer, setDisableTimer] = useState(false);

  // Fetch available jokers
  useEffect(() => {
    const fetchJokers = async () => {
      setLoadingJokers(true);
      try {
        const response = await http.get<{ jokers: BaseJoker[] }>('/loadouts/jokers/available');
        setAvailableJokers(response.jokers);
      } catch (error) {
        console.error('Failed to fetch available jokers:', error);
      } finally {
        setLoadingJokers(false);
      }
    };
    fetchJokers();
  }, []);

  // Fetch player's loadout if wallet connected
  useEffect(() => {
    if (!wallet) return;
    const fetchLoadout = async () => {
      try {
        const response = await http.get<{ loadout: { selectedStartingJoker: string | null } }>(
          `/loadouts/${wallet}`,
        );
        setSelectedJoker(response.loadout.selectedStartingJoker);
      } catch {
        // Default to no joker if fetch fails
      }
    };
    fetchLoadout();
  }, [wallet]);

  const handleStartPractice = async () => {
    try {
      await connect({ difficulty, startingJokerId: selectedJoker, disableTimer });
      navigate('/practice/match');
    } catch (error) {
      console.error('Failed to start practice match:', error);
    }
  };

  const selectedJokerData = availableJokers.find((j) => j.id === selectedJoker);

  return (
    <div className="mx-auto max-w-2xl space-y-6 py-8 px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="neon-text font-display text-3xl font-bold">Practice</h1>
          <p className="mt-2 text-sm text-white/50">
            Train against AI opponents with full game features
          </p>
        </div>

        {/* Difficulty Selection */}
        <div className="arcade-panel pixel-corners rounded-xl p-4 mb-6">
          <h2 className="font-display text-sm neon-text mb-4">SELECT DIFFICULTY</h2>
          <div className="space-y-3">
            {(Object.keys(DIFFICULTY_CONFIG) as PracticeDifficulty[]).map((level) => {
              const config = DIFFICULTY_CONFIG[level];
              const isSelected = difficulty === level;
              return (
                <button
                  key={level}
                  type="button"
                  onClick={() => setDifficulty(level)}
                  className={`w-full p-4 rounded-lg border-2 transition-all text-left ${
                    isSelected
                      ? `${config.bgColor} ${config.borderColor} shadow-lg`
                      : 'bg-black/40 border-white/10 hover:border-white/30 hover:bg-white/5'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <span
                        className={`font-display text-sm ${isSelected ? config.color : 'text-white'}`}
                      >
                        {config.label.toUpperCase()}
                      </span>
                      <p className="text-xs text-white/50 mt-1">{config.description}</p>
                    </div>
                    {isSelected && (
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        className={`w-6 h-6 rounded-full ${config.bgColor} flex items-center justify-center border ${config.borderColor}`}
                      >
                        <CheckIcon className={`w-3.5 h-3.5 ${config.color}`} />
                      </motion.div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Timer Toggle */}
          <div className="mt-4 pt-4 border-t border-white/10">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={disableTimer}
                onChange={(e) => setDisableTimer(e.target.checked)}
                className="w-5 h-5 accent-arcade-cyan rounded"
              />
              <span className="font-arcade text-sm text-white/80">
                Disable turn timer (untimed practice)
              </span>
            </label>
          </div>
        </div>

        {/* Starting Joker Selection */}
        <div className="arcade-panel pixel-corners rounded-xl p-4 mb-6">
          <button
            type="button"
            onClick={() => setJokerSectionExpanded(!jokerSectionExpanded)}
            className="w-full flex items-center justify-between"
          >
            <div className="flex items-center gap-3">
              <span className="font-display text-sm neon-text">STARTING JOKER</span>
              {selectedJokerData && (
                <span className="text-xs text-arcade-pink bg-arcade-pink/20 px-2 py-0.5 rounded border border-arcade-pink/30">
                  {selectedJokerData.name}
                </span>
              )}
              {!selectedJoker && <span className="text-xs text-white/40">None selected</span>}
            </div>
            <motion.div animate={{ rotate: jokerSectionExpanded ? 180 : 0 }}>
              <ChevronDownIcon className="w-5 h-5 text-arcade-cyan" />
            </motion.div>
          </button>

          <AnimatePresence>
            {jokerSectionExpanded && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden"
              >
                <div className="pt-4 space-y-2 border-t border-white/10 mt-4">
                  {/* None option */}
                  <button
                    type="button"
                    onClick={() => setSelectedJoker(null)}
                    className={`w-full p-3 rounded-lg border-2 text-left transition-all ${
                      selectedJoker === null
                        ? 'border-arcade-pink/50 bg-arcade-pink/10'
                        : 'border-white/10 hover:border-white/30 bg-black/20'
                    }`}
                  >
                    <span className="font-medium text-white text-sm">No Starting Joker</span>
                    <p className="text-xs text-white/40 mt-0.5">Start with no joker advantage</p>
                  </button>

                  {loadingJokers ? (
                    <div className="py-4 text-center text-arcade-cyan/60">Loading jokers...</div>
                  ) : (
                    availableJokers.map((joker) => (
                      <button
                        key={joker.id}
                        type="button"
                        onClick={() => setSelectedJoker(joker.id)}
                        className={`w-full p-3 rounded-lg border-2 text-left transition-all ${
                          selectedJoker === joker.id
                            ? 'border-arcade-pink/50 bg-arcade-pink/10'
                            : 'border-white/10 hover:border-white/30 bg-black/20'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-white text-sm">{joker.name}</span>
                          <span
                            className={`text-xs px-2 py-0.5 rounded border ${
                              joker.rarity === 'rare'
                                ? 'bg-blue-500/20 text-blue-400 border-blue-500/30'
                                : joker.rarity === 'uncommon'
                                  ? 'bg-green-500/20 text-green-400 border-green-500/30'
                                  : 'bg-gray-500/20 text-gray-400 border-gray-500/30'
                            }`}
                          >
                            {joker.rarity}
                          </span>
                        </div>
                        <p className="text-xs text-white/40 mt-1 line-clamp-2">
                          {joker.description}
                        </p>
                      </button>
                    ))
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Error Display */}
        {connectionError && (
          <div className="mb-4 p-3 rounded-lg bg-red-500/20 border border-red-500/50 text-red-400 text-sm">
            {connectionError.message}
          </div>
        )}

        {/* Start Button */}
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={handleStartPractice}
          disabled={connecting}
          className="arcade-btn pixel-corners w-full py-4 rounded-xl font-display text-sm uppercase tracking-wide text-white shadow-neon
              disabled:opacity-50 disabled:cursor-not-allowed transition-all"
        >
          {connecting ? '▸ STARTING...' : '▸ START PRACTICE MATCH'}
        </motion.button>

        {/* Info Text */}
        <p className="text-center text-arcade-cyan/50 text-xs mt-4 leading-relaxed">
          Practice matches include full game features: jokers, shop, consumables, and anomalies.
          <br />
          No wagering or blockchain transactions required.
        </p>
      </motion.div>
    </div>
  );
};
